import {
  CircleCheck,
  CircleX,
  Cpu,
  DoorOpen,
  Globe,
  TriangleAlert,
} from "lucide-react";

import jsPDF from "jspdf";
import html2canvas from "html2canvas-pro";
import { useMemo } from "react";

import logoMemoLock from "../../../assets/logo_memolock.svg";
import {
  HARDWARE_TYPE_MAP,
  HARDWARE_DOOR_COUNT_MAP,
  GET_CONTROLLER_STATUS,
} from "../../constants.js";

/*
 * Maximum number of controllers displayed on each PDF page. Reduce this value if the rows become too close together.
 */
const CONTROLLERS_PER_PAGE = 12;

/*
 * Return the readable hardware label.
 */
const getHardwareLabel = (hardwareValue) => {
  return (
    HARDWARE_TYPE_MAP[Number(hardwareValue)] ??
    (hardwareValue != null ? `Unknown (${hardwareValue})` : "-")
  );
};

/*
 * Return the safest available controller name.
 */
const getControllerName = (controller) => {
  return (
    controller?.device_info?.ssid ??
    controller?.ssid ??
    controller?.name ??
    controller?.controllerName ??
    controller?.id ??
    "Device name not updated"
  );
};

/*
 * Return the safest available controller identifier.
 */
const getControllerId = (controller) => {
  return (
    controller?.id ??
    controller?.controllerId ??
    controller?.controller_id ??
    controller?.deviceId ??
    controller?.device_id ??
    null
  );
};

/*
 * Return a numeric count from different API response formats.
 */
const getSummaryCount = (value) => {
  if (value == null) {
    return 0;
  }

  if (typeof value === "number") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.length;
  }

  if (typeof value === "object") {
    const possibleCount =
      value.count ??
      value.total ??
      value.totalCount ??
      value.quantity ??
      value.data?.length ??
      value.length ??
      0;

    const numericCount = Number(possibleCount);

    return Number.isFinite(numericCount) ? numericCount : 0;
  }

  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : 0;
};

/*
 * Divide an array into smaller groups.
 *
 * Each group is rendered as one Controller Overview page.
 */
const chunkArray = (items, size) => {
  const chunks = [];

  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }

  return chunks;
};

/*
 * Display one compact summary card.
 */
const PreviewCard = ({ icon: Icon, title, value }) => {
  return (
    <div className="flex min-h-[96px] flex-1 flex-col justify-between rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      {/* Display the icon and title on the same row. */}
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
          <Icon size={16} strokeWidth={1.8} />
        </div>

        <h2 className="text-[13px] font-semibold leading-tight text-slate-800">
          {title}
        </h2>
      </div>

      {/* Display the total below the title. */}
      <div className="mt-3 text-2xl font-bold leading-none text-slate-900">
        {value}
      </div>
    </div>
  );
};

/*
 * Display the PDF cover page.
 */
const PreviewCover = ({
  shop,
  shopLogs,
  totalControllers,
  onlineControllers,
}) => {
  /*
   * Sum the valid key transactions from every controller summary.
   */
  const validKeyTransactions = Array.isArray(shopLogs?.validKeyTransactions)
    ? shopLogs.validKeyTransactions.length
    : 0;

  /*
   * Sum the invalid key transactions from every controller summary.
   */
  const invalidKeyTransactions = Array.isArray(shopLogs?.invalidKeyTransactions)
    ? shopLogs.invalidKeyTransactions.length
    : 0;

  /*
   * Sum the intrusion alarms from every controller summary.
   */
  const intrusionAlarms = Array.isArray(shopLogs?.intrusionAlarms)
    ? shopLogs.intrusionAlarms.length
    : 0;

  /*
   * Sum the open door warnings from every controller summary.
   */
  const openDoorWarnings = Array.isArray(shopLogs?.openDoorWarnings)
    ? shopLogs.openDoorWarnings.length
    : 0;

  return (
    <section className="pdf-preview-page relative mx-auto aspect-[210/297] w-full max-w-[794px] overflow-hidden bg-white shadow-xl">
      {/* Dark cover header. */}
      <div className="h-[25%] bg-slate-800 px-12 pt-12">
        <img
          src={logoMemoLock}
          alt="MemoLock"
          className="h-15 w-auto object-contain"
        />

        <h1 className="mt-12 text-4xl font-bold tracking-tight text-white">
          Shop Security Report Logs
        </h1>

        <p className="mt-3 text-sm text-slate-300">
          Security activity and controller overview
        </p>
      </div>

      {/* Selected shop information. */}
      <div className="px-12 py-14">
        <dl className="grid grid-cols-[110px_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="font-semibold text-slate-800">Email:</dt>
          <dd className="text-slate-600">{shop?.user_email ?? "-"}</dd>

          <dt className="font-semibold text-slate-800">Project:</dt>
          <dd className="text-slate-600">{shop?.projectNumber ?? "-"}</dd>

          <dt className="font-semibold text-slate-800">Brand:</dt>
          <dd className="text-slate-600">{shop?.brand ?? "-"}</dd>

          <dt className="font-semibold text-slate-800">Shop:</dt>
          <dd className="text-slate-600">{shop?.name ?? "-"}</dd>

          <dt className="font-semibold text-slate-800">Address:</dt>
          <dd className="text-slate-600">
            {[shop?.address1, shop?.address2, shop?.city, shop?.zipcode]
              .filter(Boolean)
              .join(", ") || "-"}
          </dd>

          <dt className="font-semibold text-slate-800">Country:</dt>
          <dd className="text-slate-600">{shop?.country ?? "-"}</dd>

          <dt className="font-semibold text-slate-800">Generated:</dt>

          <dd className="text-slate-600">
            {new Date().toLocaleString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </dd>
        </dl>

        <div className="mt-10">
          <h1 className="text-2xl font-bold text-slate-900">
            Executive Summary
          </h1>

          <p className="mt-1 text-sm text-slate-500">
            A high-level view of shop activity, warnings and alarms.
          </p>

          <div className="mt-5 flex gap-4">
            <PreviewCard
              icon={CircleCheck}
              title="Valid Key Transactions"
              value={validKeyTransactions}
            />

            <PreviewCard
              icon={CircleX}
              title="Invalid Key Transactions"
              value={invalidKeyTransactions}
            />

            <PreviewCard
              icon={TriangleAlert}
              title="Intrusion Alarms"
              value={intrusionAlarms}
            />
          </div>

          <div className="mt-4 flex gap-4">
            <PreviewCard
              icon={DoorOpen}
              title="Open Door Warnings"
              value={openDoorWarnings}
            />

            <PreviewCard
              icon={Cpu}
              title="Total Controllers"
              value={totalControllers}
            />

            <PreviewCard
              icon={Globe}
              title="Online Controllers"
              value={onlineControllers}
            />
          </div>
        </div>
      </div>

      {/* Cover footer. */}
      <p className="absolute bottom-10 left-12 text-xs text-slate-400">
        Generated by MemoLock
      </p>
    </section>
  );
};

/*
 * Count the number of events for each door.
 */
const getDoorCounts = (summaryValue, totalDoors = 4) => {
  const logs = Array.isArray(summaryValue)
    ? summaryValue
    : Array.isArray(summaryValue?.data)
      ? summaryValue.data
      : [];

  const counts = Object.fromEntries(
    Array.from({ length: totalDoors }, (_, index) => [index + 1, 0]),
  );

  // Count one event for the door stored in byteParam2.
  logs.forEach((log) => {
    const door = Number(log?.byteParam2);

    // Ignore invalid door numbers.
    if (door >= 1 && door <= totalDoors) {
      counts[door] += 1;
    }
  });

  return counts;
};

const getOpenDoorCounts = (summaryValue, totalDoors = 4) => {
  const logs = Array.isArray(summaryValue)
    ? summaryValue
    : Array.isArray(summaryValue?.data)
      ? summaryValue.data
      : [];

  const counts = Object.fromEntries(
    Array.from({ length: totalDoors }, (_, index) => [index + 1, 0]),
  );

  // Assign every warning to door 1 for single-door controllers.
  if (totalDoors === 1) {
    counts[1] = getSummaryCount(summaryValue);

    return counts;
  }

  // Count one warning for the door stored in byteParam2.
  logs.forEach((log) => {
    const door = Number(log?.byteParam2);

    // Ignore invalid door numbers.
    if (door >= 1 && door <= totalDoors) {
      counts[door] += 1;
    }
  });

  return counts;
};

/*
 * Display one controller row.
 *
 * Each row loads the logs for its own controller.
 */
const ControllerOverviewRow = ({ controller, shopLogs }) => {
  const controllerId = getControllerId(controller);

  // Read the summary generated for this controller.
  const deviceSummary = shopLogs?.controllerSummaries?.[controllerId] ?? {};

  // Filter the shop logs only when the source data or controller ID changes.
  const shopOpenDoorWarnings = useMemo(() => {
    const openDoorWarnings = shopLogs?.openDoorWarnings ?? [];

    return openDoorWarnings.filter((log) =>
      isLogFromController(log, controllerId),
    );
  }, [shopLogs?.openDoorWarnings, controllerId]);

  const hardwareValue =
    controller?.device_info?.hardware_v ?? controller?.hardware_v;

  const totalDoors = HARDWARE_DOOR_COUNT_MAP[Number(hardwareValue)] ?? 1;

  const status = GET_CONTROLLER_STATUS(controller);

  // Read the intrusion alarm summary for this controller.
  const intrusionSummary = deviceSummary?.intrusionAlarms;

  // Count intrusion alarms for each door.
  const intrusionCounts = getDoorCounts(intrusionSummary, totalDoors);

  // Count the total intrusion alarms.
  const intrusionTotal = getSummaryCount(intrusionSummary);

  // Read the open door warning summary for this controller.
  const openDoorSummary =
    getSummaryCount(deviceSummary?.openDoorWarnings) > 0
      ? deviceSummary.openDoorWarnings
      : shopOpenDoorWarnings;

  // Count open door warnings for each door.
  const openDoorCounts = getOpenDoorCounts(openDoorSummary, totalDoors);

  // Count the total open door warnings.
  const openDoorTotal = getSummaryCount(openDoorSummary);

  const displayValue = (summaryKey) => {
    return getSummaryCount(deviceSummary?.[summaryKey]);
  };

  const statusClasses =
    status === "Online"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : "border-slate-300 bg-slate-100 text-slate-600";

  return (
    <tr className="even:bg-slate-50">
      <td className="border-r border-slate-200 px-1 py-3 text-center text-slate-600">
        {getControllerName(controller)}
      </td>

      <td className="border-r border-slate-200 px-1 py-3 text-center text-slate-600">
        {getHardwareLabel(hardwareValue)}
      </td>

      <td className="border-r border-slate-200 px-1 py-3 text-center text-slate-600">
        {displayValue("chipKeyTransaction")}
      </td>

      <td className="border-r border-slate-200 px-1 py-3 text-center text-slate-600">
        {displayValue("invalidKeyTransaction")}
      </td>

      <td className="border-r border-slate-200 px-1 py-3 text-center text-slate-600">
        <div className="flex w-full items-stretch whitespace-nowrap">
          <div className="mr-3 flex w-8 shrink-0 items-center justify-center border-r border-slate-300 pr-1">
            <span className="text-slate-700">{intrusionTotal}</span>
          </div>

          {totalDoors > 0 ? (
            <div
              className="grid justify-items-center gap-x-3 gap-y-1"
              style={{
                gridTemplateColumns: `repeat(${Math.min(totalDoors, 4)}, 20px)`,
              }}
            >
              {Array.from(
                { length: Math.min(totalDoors, 4) },
                (_, index) => index + 1,
              ).map((door) => (
                <span
                  key={`intrusion-label-top-${door}`}
                  className="w-5 text-center text-[8px] font-semibold text-slate-500"
                >
                  D{door}
                </span>
              ))}

              {Array.from({ length: totalDoors }, (_, index) => index + 1).map(
                (door) => (
                  <div
                    key={`intrusion-value-${door}`}
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                      (intrusionCounts[door] ?? 0) > 0
                        ? "border-red-400 bg-red-100"
                        : "border-slate-300 bg-white"
                    }`}
                  >
                    <span className="text-[9px] font-semibold text-stone-700">
                      {Math.round((intrusionCounts[door] ?? 0) * 10) / 10}
                    </span>
                  </div>
                ),
              )}

              {totalDoors === 8 &&
                Array.from({ length: 4 }, (_, index) => index + 5).map(
                  (door) => (
                    <span
                      key={`intrusion-label-bottom-${door}`}
                      className="w-5 text-center text-[8px] font-semibold text-slate-500"
                    >
                      D{door}
                    </span>
                  ),
                )}
            </div>
          ) : (
            <span className="ml-auto text-slate-400">-</span>
          )}
        </div>
      </td>

      <td className="border-r border-slate-200 px-1 py-3 text-center text-slate-600">
        <div className="flex w-full items-stretch whitespace-nowrap">
          <div className="mr-2 flex w-8 shrink-0 items-center justify-center border-r border-slate-300 pr-1">
            <span className="text-stone-700">{openDoorTotal}</span>
          </div>

          {totalDoors > 0 ? (
            <div
              className="grid justify-items-center gap-x-3 gap-y-1"
              style={{
                gridTemplateColumns: `repeat(${Math.min(totalDoors, 4)}, 20px)`,
              }}
            >
              {/* Display labels for the first four doors. */}
              {Array.from(
                { length: Math.min(totalDoors, 4) },
                (_, index) => index + 1,
              ).map((door) => (
                <span
                  key={`open-door-label-top-${door}`}
                  className="w-5 text-center text-[8px] font-semibold text-slate-500"
                >
                  D{door}
                </span>
              ))}

              {/* Display values for the first four doors. */}
              {Array.from(
                { length: Math.min(totalDoors, 4) },
                (_, index) => index + 1,
              ).map((door) => {
                const doorCount = openDoorCounts[door] ?? 0;
                const isActive = doorCount > 0;

                return (
                  <div
                    key={`open-door-value-top-${door}`}
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                      isActive
                        ? "border-orange-400 bg-orange-100"
                        : "border-slate-300 bg-white"
                    }`}
                  >
                    <span className="text-[9px] font-semibold text-stone-700">
                      {doorCount}
                    </span>
                  </div>
                );
              })}

              {/* Display the second row for eight-door controllers. */}
              {totalDoors === 8 && (
                <>
                  {Array.from({ length: 4 }, (_, index) => index + 5).map(
                    (door) => (
                      <span
                        key={`open-door-label-bottom-${door}`}
                        className="w-5 text-center text-[8px] font-semibold text-slate-500"
                      >
                        D{door}
                      </span>
                    ),
                  )}

                  {Array.from({ length: 4 }, (_, index) => index + 5).map(
                    (door) => {
                      const doorCount = openDoorCounts[door] ?? 0;
                      const isActive = doorCount > 0;

                      return (
                        <div
                          key={`open-door-value-bottom-${door}`}
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                            isActive
                              ? "border-orange-400 bg-orange-100"
                              : "border-slate-300 bg-white"
                          }`}
                        >
                          <span className="text-[9px] font-semibold text-stone-700">
                            {doorCount}
                          </span>
                        </div>
                      );
                    },
                  )}
                </>
              )}
            </div>
          ) : (
            <span className="ml-auto text-slate-400">-</span>
          )}
        </div>
      </td>

      <td className="px-1 py-3 text-center">
        <span
          className={`inline-flex rounded-full border px-2 py-0.5 font-semibold ${statusClasses}`}
        >
          {status}
        </span>
      </td>
    </tr>
  );
};

/*
 * Display the Controller Overview table.
 */
const ControllerOverviewTable = ({ controllers, shopLogs }) => {
  return (
    <div className="mt-3 overflow-hidden rounded-lg border border-slate-200">
      <table className="w-full table-fixed text-left text-[10px]">
        <thead className="bg-slate-800 text-white">
          <tr>
            <th className="w-[17%] border-r border-slate-200 px-2 py-2.5 font-semibold">
              Device Name
            </th>

            <th className="w-[12%] border-r border-slate-200 px-2 py-2.5 text-center font-semibold">
              Hardware
            </th>

            <th className="w-[6%] border-r border-slate-200 px-1 py-2.5 text-center font-semibold">
              Valid
            </th>

            <th className="w-[6%] border-r border-slate-200 px-1 py-2.5 text-center font-semibold">
              Invalid
            </th>

            <th className="w-[24%] border-r border-slate-200 px-2 py-2.5 text-center font-semibold">
              Intrusions Alarm
            </th>

            <th className="w-[25%] border-r border-slate-200 px-2 py-2.5 text-center font-semibold">
              Open Door Warnings
            </th>

            <th className="w-[10%] px-2 py-2.5 text-center font-semibold">
              Status
            </th>
          </tr>
        </thead>

        <tbody className="divide-y divide-slate-200">
          {controllers.length > 0 ? (
            controllers.map((controller, index) => (
              <ControllerOverviewRow
                key={
                  getControllerId(controller) ??
                  `${getControllerName(controller)}-${index}`
                }
                controller={controller}
                shopLogs={shopLogs}
              />
            ))
          ) : (
            <tr>
              <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                No controllers found for this shop.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

/*
 * Display the first Executive Summary page.
 */
const PreviewSummaryFirstPage = ({
  shopLogs,
  controllers,
  pageNumber,
  totalPages,
}) => {
  return (
    <section className="pdf-preview-page relative mx-auto aspect-[210/297] w-full max-w-[794px] overflow-hidden bg-white px-10 py-10 shadow-xl">
      {/* Page heading. */}
      <header>
        <h1 className="text-2xl font-bold text-slate-900">
          Controller Overview
        </h1>

        <p className="mt-1 text-sm text-slate-500">
          Detailed controller activity and status.
        </p>
      </header>

      {/* Controller Overview table. */}
      <div className="mt-9">
        <ControllerOverviewTable
          controllers={controllers}
          shopLogs={shopLogs}
        />
      </div>

      {/* Page number. */}
      <div className="absolute bottom-8 left-0 right-0 text-center text-[10px] text-slate-400">
        Page {pageNumber} of {totalPages}
      </div>
    </section>
  );
};

/*
 * Display a continuation Controller Overview page.
 */
const PreviewControllerContinuationPage = ({
  controllers,
  shopLogs,
  pageNumber,
  totalPages,
}) => {
  return (
    <section className="pdf-preview-page relative mx-auto aspect-[210/297] w-full max-w-[794px] overflow-hidden bg-white px-10 py-10 shadow-xl">
      {/* Continuation page heading. */}
      <header>
        <h1 className="text-2xl font-bold text-slate-900">
          Controller Overview
        </h1>

        <p className="mt-1 text-sm text-slate-500">
          Continued controller activity details.
        </p>
      </header>

      <div className="mt-7">
        <ControllerOverviewTable
          controllers={controllers}
          shopLogs={shopLogs}
        />
      </div>

      {/* Page number. */}
      <div className="absolute bottom-8 left-0 right-0 text-center text-[10px] text-slate-400">
        Page {pageNumber} of {totalPages}
      </div>
    </section>
  );
};

/*
 * Check whether a log belongs to a specific controller.
 */
const isLogFromController = (log, controllerId) => {
  const logDeviceId =
    log?.rowKey ??
    log?.deviceId ??
    log?.device_id ??
    log?.controllerId ??
    log?.controller_id;

  return (
    String(logDeviceId ?? "")
      .trim()
      .toLowerCase() ===
    String(controllerId ?? "")
      .trim()
      .toLowerCase()
  );
};

/*
 * Return true when the controller has a security alert.
 */
const hasControllerNotification = (controller, shopLogs) => {
  const controllerId = getControllerId(controller);

  // Read the summary generated for this controller.
  const summary = shopLogs?.controllerSummaries?.[controllerId] ?? {};

  // Only intrusion alarms and open door warnings affect the priority.
  const intrusions = getSummaryCount(summary?.intrusionAlarms);

  const openDoors = getSummaryCount(
    summary?.openDoorWarnings ?? summary?.doorOpenWarnings,
  );

  // Check fallback open door warnings stored in the shop logs.
  const fallbackOpenDoors = (shopLogs?.openDoorWarnings ?? []).filter((log) =>
    isLogFromController(log, controllerId),
  ).length;

  // Controllers with security alerts must appear first.
  return intrusions > 0 || openDoors > 0 || fallbackOpenDoors > 0;
};

/*
 * Display the report preview inside a modal.
 */
const PdfPreview = ({ shop, shopLogs, controllers = [], onClose }) => {
  /*
   * Divide the controllers into printable pages.
   */
  // Sort controllers by security alerts first, then by online status.
  const sortedControllers = [...controllers].sort((a, b) => {
    // Check whether each controller has an intrusion or open door warning.
    const aHasSecurityAlert = hasControllerNotification(a, shopLogs);
    const bHasSecurityAlert = hasControllerNotification(b, shopLogs);

    // Controllers with security alerts always appear first.
    if (aHasSecurityAlert !== bHasSecurityAlert) {
      return aHasSecurityAlert ? -1 : 1;
    }

    // If both controllers have the same alert priority, check their status.
    const aIsOnline = GET_CONTROLLER_STATUS(a) === "Online";
    const bIsOnline = GET_CONTROLLER_STATUS(b) === "Online";

    // Online controllers appear before offline controllers.
    if (aIsOnline !== bIsOnline) {
      return aIsOnline ? -1 : 1;
    }

    // Keep the original order when both controllers have the same priority.
    return 0;
  });

  // Divide the sorted controllers into printable pages.
  const controllerPages = chunkArray(sortedControllers, CONTROLLERS_PER_PAGE);

  /*
   * Always create at least one Controller Overview page.
   */
  const safeControllerPages =
    controllerPages.length > 0 ? controllerPages : [[]];

  /*
   * Cover page plus every Controller Overview page.
   */
  const totalPages = 1 + safeControllerPages.length;

  // Count controllers that are currently online.
  const onlineControllers = controllers.filter(
    (controller) => GET_CONTROLLER_STATUS(controller) === "Online",
  ).length;
  /*
   * Export every visible preview page to an A4 PDF.
   */
  const handleDownloadPreview = async () => {
    try {
      const previewPages = document.querySelectorAll(".pdf-preview-page");

      if (previewPages.length === 0) {
        throw new Error("No PDF preview pages were found");
      }

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();

      const pdfHeight = pdf.internal.pageSize.getHeight();

      for (let pageIndex = 0; pageIndex < previewPages.length; pageIndex += 1) {
        const previewPage = previewPages[pageIndex];

        const canvas = await html2canvas(previewPage, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
        });

        const imageData = canvas.toDataURL("image/png");

        if (pageIndex > 0) {
          pdf.addPage();
        }

        pdf.addImage(imageData, "PNG", 0, 0, pdfWidth, pdfHeight);
      }

      /*
       * Build a safe PDF filename.
       */
      const safeShopName = String(shop?.name || "shop")
        .replace(/[^a-z0-9]/gi, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "")
        .toLowerCase();

      pdf.save(`shop-security-report-${safeShopName || "shop"}.pdf`);

      onClose?.();
    } catch (error) {
      console.error("Unable to export the PDF preview:", error);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
      {/* Close the preview when the backdrop is clicked. */}
      <button
        type="button"
        aria-label="Close PDF preview"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />

      {/* Keep modal content above the backdrop. */}
      <div className="relative flex max-h-[95vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-slate-100 shadow-2xl">
        {/* Preview toolbar. */}
        <header className="flex shrink-0 items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
          <div>
            <h1 className="text-lg font-bold text-slate-900">
              PDF Report Preview
            </h1>

            <p className="text-sm text-slate-500">
              Review the report before downloading it.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleDownloadPreview}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
            >
              Download PDF
            </button>
          </div>
        </header>

        {/* Display all printable report pages. */}
        <main className="flex-1 overflow-y-auto p-8">
          <div className="space-y-10">
            <PreviewCover
              shop={shop}
              shopLogs={shopLogs}
              totalControllers={controllers.length}
              onlineControllers={onlineControllers}
            />

            {safeControllerPages.map((controllerPage, index) => {
              const reportPageNumber = index + 2;

              if (index === 0) {
                return (
                  <PreviewSummaryFirstPage
                    key="summary-page"
                    shopLogs={shopLogs}
                    controllers={controllerPage}
                    pageNumber={reportPageNumber}
                    totalPages={totalPages}
                  />
                );
              }

              return (
                <PreviewControllerContinuationPage
                  key={`controller-page-${index}`}
                  controllers={controllerPage}
                  shopLogs={shopLogs}
                  pageNumber={reportPageNumber}
                  totalPages={totalPages}
                />
              );
            })}
          </div>
        </main>
      </div>
    </div>
  );
};

export default PdfPreview;
